/**
 * The correction half of auto-skin v2: leak checking, automatic leak fixing, one-click region
 * reassignment, the masks the viewport paints with, and the statistics the SKIN panel shows.
 *
 * Split out of `rigController.ts` so that file stays about the *pipeline* (cache → solve →
 * assemble) and this one about *fixing what the pipeline got wrong*. Everything here reaches the
 * controller's live state through `SkinCorrectionHost`; it owns no meshes of its own.
 *
 * Every entry point starts with `host.ensureCache()`: after a project reload the weights are
 * restored from disk and there is no distance cache at all, and none of these tools can work
 * without one (auto-skin v2 amendment A4).
 */

import type { BufferGeometry } from 'three';

import { AppError, CANONICAL_BONE_IDS } from '../../shared';
import type {
  CanonicalBoneId,
  SkinData,
  SkinParams,
  SkinRegionOverride,
  SkinResolution,
} from '../../shared';
import { boneInfluenceMask } from '../../rig/skinning';
import type { BoneSegment } from '../../rig/skinning';
import type { BuiltCanonicalSkeleton } from '../../rig/skeletonBuilder';
import { cloneSkinParams } from '../../rig/autoskin/skinParams';
import { boneMatricesForPose, deformVertices, testPoseById } from '../../rig/autoskin/testPoses';
import { detectLeaks, fixLeaks as fixLeaksPure } from '../../rig/autoskin/leaks';
import { regionOfVertex, solveSkinWeights } from '../../rig/autoskin/weightSolver';
import type { SolveStats } from '../../rig/autoskin/weightSolver';
import type { TestPoseId, VertexDistanceCache } from '../../rig/autoskin/types';
import type { EditorContext, SkinGenerationStats } from '../context';
import type { LeakSummary } from '../types';
import { guard } from './internal';

/** `skinIndex` values index `skeleton.bones`, which is `CANONICAL_BONE_IDS` order (plan D4). */
export function boneIndexOf(id: CanonicalBoneId): number {
  return CANONICAL_BONE_IDS.indexOf(id);
}

/** One mesh's model-space vertex positions, flat xyz. */
export function readMeshPositions(geometry: BufferGeometry): Float32Array {
  const attribute = geometry.getAttribute('position');
  if (!attribute) {
    throw new AppError('SKIN_NO_POSITION', 'The mesh geometry has no position attribute.');
  }
  const out = new Float32Array(attribute.count * 3);
  for (let v = 0; v < attribute.count; v++) {
    out[v * 3] = attribute.getX(v);
    out[v * 3 + 1] = attribute.getY(v);
    out[v * 3 + 2] = attribute.getZ(v);
  }
  return out;
}

/** Roll the per-mesh cache and solve numbers up into the one object the panel displays. */
function aggregateStats(
  caches: readonly VertexDistanceCache[],
  solves: readonly SolveStats[],
  resolution: SkinResolution,
): SkinGenerationStats {
  const stats: SkinGenerationStats = {
    resolution,
    closed: true,
    surface: 0,
    interior: 0,
    buildMs: 0,
    geodesicMs: 0,
    solveMs: 0,
    pulledInside: [],
    unreachable: 0,
    fallbackVertices: 0,
    nonFinite: 0,
    vertexCount: 0,
    uniqueCount: 0,
  };
  const pulled = new Set<CanonicalBoneId>();
  for (const cache of caches) {
    stats.surface += cache.gridStats.surface;
    stats.interior += cache.gridStats.interior;
    stats.buildMs += cache.gridStats.buildMs;
    stats.geodesicMs += cache.gridStats.geodesicMs;
    stats.vertexCount += cache.vertexCount;
    stats.uniqueCount += cache.uniqueCount;
    if (!cache.gridStats.closed) stats.closed = false;
    for (const id of cache.gridStats.pulledInside) pulled.add(id);
  }
  for (const solve of solves) {
    stats.solveMs += solve.solveMs;
    stats.unreachable += solve.unreachable;
    stats.fallbackVertices += solve.fallbackVertices;
    stats.nonFinite += solve.nonFinite;
  }
  stats.pulledInside = [...pulled];
  return stats;
}

/** The slice of the rig controller's internal state the correction tools read. */
export interface SkinCorrectionHost {
  ctx: EditorContext;
  /** One distance cache per source mesh; `null` for a mesh whose cache is not built. */
  caches(): (VertexDistanceCache | null)[];
  /** The authoritative weights, index-aligned with `caches()`. */
  skins(): SkinData[];
  /** Model-space rest positions of one mesh (flat xyz), or null when there is no such mesh. */
  restPositions(meshIndex: number): Float32Array | null;
  /** Bone segments of the placement the caches were built for. */
  segments(): BoneSegment[];
  builtSkeleton(): BuiltCanonicalSkeleton | null;
  /** Build the distance cache if there is none. */
  ensureCache(): Promise<void>;
  /**
   * Re-solve every mesh from the cache, push the weights into the live geometry and force the
   * project write. It deliberately does NOT touch the leak report: each caller decides whether to
   * refresh it (`refreshAfterSolve`) or to run its own, more specific check (C1).
   */
  resolveFromCache(): Promise<void>;
  /** Enter/leave a "the thread is about to block" phase. Nestable — the host counts (C3). */
  beginBusy(): void;
  endBusy(): void;
  /** Give the browser a chance to paint the busy state before blocking (C5). */
  yieldToUi(): Promise<void>;
}

export interface SkinCorrection {
  runLeakCheck(poseId: TestPoseId): Promise<LeakSummary | null>;
  fixLeaks(poseId: TestPoseId): Promise<LeakSummary | null>;
  revertLastFix(): Promise<void>;
  reassignRegionAt(
    meshIndex: number,
    vertexIndex: number,
    boneId: CanonicalBoneId,
  ): Promise<void>;
  clearRegionOverrides(): Promise<void>;
  /**
   * Called by the controller after EVERY re-solve, including the throttled ones a slider drag
   * produces: the leak report now describes weights that no longer exist. This only sets a flag —
   * it allocates nothing, scans nothing and dispatches nothing, so a drag stays cheap (C1).
   */
  markLeakStale(): void;
  /**
   * Called on the committing paths only (public `resolveFromCache`, region reassignment, revert).
   * With a pose previewed the check is re-run for it (deform + detect, no solve), otherwise the
   * summary and the masks are dropped. A no-op when nothing has gone stale.
   */
  refreshAfterSolve(): Promise<void>;
  leakMask(meshIndex?: number): Float32Array | null;
  influenceMask(boneIndex: number, meshIndex?: number): Float32Array | null;
  skinStats(): SkinGenerationStats | null;
  /** Called by the controller after every solve, with the caches and stats it just used. */
  recordSolve(
    caches: readonly VertexDistanceCache[],
    solves: readonly SolveStats[],
    resolution: SkinResolution,
  ): void;
  /** Forget the leak masks and the statistics (new generation, project close). */
  reset(): void;
}

function millimetres(metres: number): string {
  return (metres * 1000).toFixed(1);
}

export function createSkinCorrection(host: SkinCorrectionHost): SkinCorrection {
  const { ctx } = host;
  const { store } = ctx;

  /** Per-mesh 1/0 mask of the last leak check; index-aligned with the meshes. */
  let leakMasks: (Float32Array | null)[] = [];
  /** The parameters `fixLeaks` started from, for `revertLastFix`. */
  let paramsBeforeFix: SkinParams | null = null;
  /** Cost + quality of the last solve; `null` until one has run. */
  let stats: SkinGenerationStats | null = null;
  /** Set by every re-solve: the standing leak report describes weights that are gone (C1). */
  let leakStale = false;

  /**
   * How far a vertex may sit inside the transition band before a displacement counts as a leak.
   * It has to track the solver's own falloff width: with a wide blend the legitimate blend zone is
   * wide too, and a fixed 2 cm band reported half of it as leaking (C4).
   */
  function blendZoneFor(blend: number): number {
    return Math.max(2 * blend, 0.01);
  }

  /**
   * Bring the leak report in line with the weights that are actually on the mesh. Cheap when
   * nothing changed (a flag test) and when nothing is previewed (a clear).
   */
  async function refreshAfterSolve(): Promise<void> {
    if (!leakStale) return;
    leakStale = false;
    const state = store.getState();
    const pose = state.rig.previewPose;
    if (state.rig.leakSummary && pose) {
      // Cheap: the cache and the skeleton are already there, only the deform + detect re-runs.
      await check(pose, true);
      return;
    }
    leakMasks = [];
    if (state.rig.leakSummary) store.actions.setLeakSummary(null);
  }

  async function check(poseId: TestPoseId, quiet: boolean): Promise<LeakSummary | null> {
    const skins = host.skins();
    const built = host.builtSkeleton();
    if (skins.length === 0 || !built) return null;

    const blendZone = blendZoneFor(store.getState().rig.skinParams.blend);

    await host.ensureCache();
    const caches = host.caches();
    const pose = testPoseById(poseId);
    // `boneMatricesForPose` poses the live skeleton and restores it before returning; the
    // viewport re-applies the preview pose every frame, so the flicker is not observable.
    const matrices = boneMatricesForPose(built, pose.pose);

    const masks: (Float32Array | null)[] = [];
    let count = 0;
    let maxDisplacement = 0;

    for (let i = 0; i < skins.length; i++) {
      const cache = caches[i];
      const rest = host.restPositions(i);
      if (!cache || !rest) {
        masks.push(null);
        continue;
      }
      const deformed = deformVertices(rest, skins[i], matrices);
      const report = detectLeaks(cache, skins[i], rest, deformed, pose, { blendZone });
      const mask = new Float32Array(skins[i].vertexCount);
      for (const v of report.leaked) mask[v] = 1;
      masks.push(mask);
      count += report.count;
      if (report.maxDisplacement > maxDisplacement) maxDisplacement = report.maxDisplacement;
    }

    leakMasks = masks;
    leakStale = false;
    const summary: LeakSummary = { poseId, count, maxDisplacement };
    // A drag that changes nothing measurable must not churn the store: an equal summary is the
    // same fact, and dispatching a new object would re-render every subscriber (C1).
    const current = store.getState().rig.leakSummary;
    const unchanged =
      current !== null &&
      current.poseId === summary.poseId &&
      current.count === summary.count &&
      current.maxDisplacement === summary.maxDisplacement;
    if (!unchanged) store.actions.setLeakSummary(summary);
    if (!quiet) {
      store.actions.toast(
        'info',
        count === 0
          ? `No leaks in "${pose.label}".`
          : `Leaks: ${count} vertices (max ${millimetres(maxDisplacement)} mm)`,
      );
    }
    return summary;
  }

  return {
    async runLeakCheck(poseId: TestPoseId): Promise<LeakSummary | null> {
      return guard(ctx, 'The leak check could not be run.', () => check(poseId, false));
    },

    async fixLeaks(poseId: TestPoseId): Promise<LeakSummary | null> {
      return guard(ctx, 'The leaks could not be fixed automatically.', async () => {
        const skins = host.skins();
        const built = host.builtSkeleton();
        if (skins.length === 0 || !built) return null;

        await host.ensureCache();
        const cache = host.caches()[0];
        const rest = host.restPositions(0);
        if (!cache || !rest) return null;

        const before = cloneSkinParams(store.getState().rig.skinParams);
        const pose = testPoseById(poseId);
        const matrices = boneMatricesForPose(built, pose.pose);
        const segments = host.segments();

        // "Before" is measured over EVERY mesh, exactly like the "after" below. Taking it from the
        // mesh-0 search instead made the toast compare two different populations, so a fix could
        // read as "12 → 30" while it had in fact improved things (F11).
        const baseline = await check(poseId, true);

        // The search itself re-solves mesh 0 only: it is the body mesh in every model this tool is
        // for, and a full multi-mesh solve per iteration would make the button unusable.
        const evaluate = (candidate: SkinParams) => {
          const skin = solveSkinWeights(cache, candidate, boneIndexOf, {
            positions: rest,
            segments,
          });
          const deformed = deformVertices(rest, skin, matrices);
          return detectLeaks(cache, skin, rest, deformed, pose, {
            blendZone: blendZoneFor(candidate.blend),
          });
        };

        // Up to ten solve+detect rounds block the thread; announce it and let the panel paint
        // first, exactly like the voxelizer does (C5).
        host.beginBusy();
        let best: ReturnType<typeof fixLeaksPure>;
        try {
          await host.yieldToUi();
          best = fixLeaksPure(cache, before, pose, evaluate);
        } finally {
          host.endBusy();
        }
        paramsBeforeFix = before;

        store.actions.setSkinParams(best.params);
        await host.resolveFromCache();
        // Its own, more specific refresh: the same aggregate check the "before" came from, so the
        // toast compares like with like and `refreshAfterSolve` has nothing left to do.
        const after = await check(poseId, true);

        const from = baseline?.count ?? 0;
        const to = after?.count ?? best.report.count;
        store.actions.toast(
          'info',
          from === to ? `Leaks ${from} → ${to} (no improvement found)` : `Leaks ${from} → ${to}`,
        );
        return after;
      });
    },

    async revertLastFix(): Promise<void> {
      return guard(ctx, 'The last leak fix could not be reverted.', async () => {
        if (!paramsBeforeFix) return;
        store.actions.setSkinParams(paramsBeforeFix);
        paramsBeforeFix = null;
        await host.resolveFromCache();
        await refreshAfterSolve();
      });
    },

    async reassignRegionAt(
      meshIndex: number,
      vertexIndex: number,
      boneId: CanonicalBoneId,
    ): Promise<void> {
      return guard(ctx, 'The region could not be reassigned.', async () => {
        const skins = host.skins();
        const skin = skins[meshIndex];
        if (!skin) return;

        await host.ensureCache();
        const cache = host.caches()[meshIndex];
        if (!cache) return;

        const region = regionOfVertex(cache, skin, vertexIndex);
        if (region.length === 0) {
          store.actions.toast('info', 'No region found under that click.');
          return;
        }

        const params = store.getState().rig.skinParams;
        const claimed = new Set(region);
        // Dedupe: a vertex belongs to exactly one override, so an earlier override of the same
        // mesh loses whatever this one takes, and an emptied override disappears entirely.
        const overrides: SkinRegionOverride[] = [];
        for (const existing of params.regionOverrides) {
          if (existing.meshName !== cache.meshName) {
            overrides.push(existing);
            continue;
          }
          const kept = existing.vertices.filter((v) => !claimed.has(v));
          if (kept.length > 0) overrides.push({ ...existing, vertices: kept });
        }
        overrides.push({ meshName: cache.meshName, vertices: region, boneId });

        store.actions.setSkinParams({ ...cloneSkinParams(params), regionOverrides: overrides });
        await host.resolveFromCache();
        await refreshAfterSolve();
        store.actions.toast('info', `${region.length} vertices reassigned to ${boneId}.`);
      });
    },

    async clearRegionOverrides(): Promise<void> {
      return guard(ctx, 'The region overrides could not be cleared.', async () => {
        const params = store.getState().rig.skinParams;
        if (params.regionOverrides.length === 0) return;
        store.actions.setSkinParams({ ...cloneSkinParams(params), regionOverrides: [] });
        await host.resolveFromCache();
        await refreshAfterSolve();
      });
    },

    markLeakStale(): void {
      leakStale = true;
    },

    refreshAfterSolve,

    leakMask(meshIndex = 0): Float32Array | null {
      return leakMasks[meshIndex] ?? null;
    },

    influenceMask(boneIndex: number, meshIndex = 0): Float32Array | null {
      const skin = host.skins()[meshIndex];
      return skin ? boneInfluenceMask(skin, boneIndex) : null;
    },

    skinStats(): SkinGenerationStats | null {
      return stats;
    },

    recordSolve(caches, solves, resolution): void {
      stats = caches.length === 0 ? null : aggregateStats(caches, solves, resolution);
    },

    reset(): void {
      leakMasks = [];
      paramsBeforeFix = null;
      stats = null;
      leakStale = false;
    },
  };
}
